<script setup lang="ts">
import { computed, onMounted } from "vue";

import type { TFile } from "@/api/shared/file";
import FileExtensionIcon from "@/components/shared/files/FileExtensionIcon.vue";
import UploadFieldBody from "@/components/ui/inputs/UploadFieldBody.vue";
import { EFileType, getFileType } from "@/composables/shared/useFile";
import { useIsTouch } from "@/composables/shared/useIsTouch";
import {
  type TUploadFieldSharedProps,
  useUploadFieldBase,
} from "@/composables/ui/inputs/useUploadFieldBase";
import AppImg from "@/components/shared/files/AppImg.vue";

const props = withDefaults(defineProps<TUploadFieldSharedProps>(), {
  multiple: true,
});

const {
  t,
  acceptAttribute,
  getUploadProgress,
  uploadedFiles,
  fileInput,
  isDragOver,
  isHovered,
  isUploading,
  initFromFieldValue,
  uploadMultipleFiles,
  onRemove,
  openFileBrowser,
  getDisplayFileSize,
  isFileDisabled,
  onDragOver,
  onDragEnter,
  onDragLeave,
  onMouseEnter,
  onMouseLeave,
} = useUploadFieldBase(props);

const isTouch = useIsTouch();

const hasFiles = computed(() => uploadedFiles.value.length > 0);

async function handlePickedFiles(selectedFiles: File[]) {
  if (!selectedFiles.length || props.disabled) return;

  await uploadMultipleFiles(selectedFiles);

  if (fileInput.value) {
    fileInput.value.value = "";
  }
}

async function onNativeInputChange(event: Event) {
  const target = event.target as HTMLInputElement | null;
  const files = Array.from(target?.files ?? []);
  await handlePickedFiles(files);
}

async function onDrop(event: DragEvent) {
  event.preventDefault();
  isDragOver.value = false;

  if (props.disabled) return;

  const files = Array.from(event.dataTransfer?.files ?? []);
  await handlePickedFiles(files);
}

onMounted(async () => {
  await initFromFieldValue();
});

function progressFor(file: TFile) {
  return getUploadProgress(file);
}

const hasInlinePreview = (file: TFile) => {
  return getFileType(file) === EFileType.IMAGE && !!file.file;
};
</script>

<template>
  <div class="tw:flex tw:h-full! tw:w-full! tw:min-w-0 tw:flex-col tw:overflow-hidden">
    <input
      ref="fileInput"
      type="file"
      class="tw:hidden"
      multiple
      :accept="acceptAttribute"
      :disabled="props.disabled || isUploading"
      @change="onNativeInputChange"
    />

    <div
      role="button"
      :tabindex="props.disabled || isUploading ? -1 : 0"
      :aria-disabled="props.disabled || isUploading"
      class="upload-dropzone tw:relative tw:mb-2 tw:flex tw:min-h-[150px] tw:sm:min-h-[250px] tw:w-full tw:min-w-0 tw:items-center tw:justify-center tw:overflow-hidden tw:rounded tw:border tw:border-dashed tw:border-on-surface/20 tw:bg-transparent tw:p-4 tw:text-center"
      :class="{
        'upload-dropzone--drag': isDragOver,
        'tw:cursor-not-allowed tw:opacity-60': props.disabled || isUploading,
        'tw:cursor-pointer': !props.disabled && !isUploading,
        'upload-dropzone--hover': isHovered,
      }"
      @click="openFileBrowser"
      @keydown.enter.prevent="openFileBrowser"
      @keydown.space.prevent="openFileBrowser"
      @dragover="onDragOver"
      @dragenter="onDragEnter"
      @dragleave="onDragLeave"
      @mouseenter="onMouseEnter"
      @mouseleave="onMouseLeave"
      @drop="onDrop"
    >
      <upload-field-body
        :is-hovered="isHovered"
        :icon="icon"
        :img-src="imgSrc"
        :theme-aware-icon="props.themeAwareIcon"
        :label="label"
        :optional="optional"
      />
    </div>

    <div
      v-if="hasFiles"
      class="tw:flex tw:min-h-0 tw:w-full tw:min-w-0 tw:flex-1 tw:flex-col tw:overflow-hidden"
    >
      <div class="tw:flex tw:min-h-0 tw:w-full tw:min-w-0 tw:flex-1 tw:flex-col tw:overflow-auto">
        <v-menu
          v-for="file in uploadedFiles"
          :key="file.id"
          open-on-hover
          location="end center"
          origin="auto"
          :open-delay="650"
          :close-delay="120"
          :disabled="props.disabled || isTouch || !hasInlinePreview(file)"
        >
          <template #activator="{ props: menuProps }">
            <div
              v-bind="menuProps"
              class="tw:mb-2 tw:flex tw:min-w-0 tw:items-center tw:gap-3 tw:rounded tw:border tw:border-on-surface/10 tw:bg-surface tw:p-2 tw:transition-colors tw:hover:bg-surface-muted"
            >
              <div
                class="tw:flex tw:shrink-0 tw:items-center tw:justify-center tw:overflow-hidden tw:rounded tw:bg-surface-muted"
              >
                <file-extension-icon
                  :file="file"
                  show-image
                />
              </div>

              <div class="tw:min-w-0 tw:flex-1">
                <div class="tw:truncate tw:text-sm tw:font-medium tw:text-on-surface">
                  {{ file.name }}
                </div>

                <div class="tw:text-xs tw:text-medium-emphasis">
                  {{ getDisplayFileSize(file) }}
                </div>

                <v-progress-linear
                  v-if="progressFor(file)"
                  class="tw:mt-2"
                  :model-value="progressFor(file)?.progress ?? 0"
                  height="6"
                  rounded
                />
              </div>

              <div class="tw:flex tw:shrink-0 tw:items-center tw:gap-1">
                <v-chip
                  v-if="isFileDisabled(file)"
                  size="small"
                  variant="tonal"
                  color="grey"
                  prepend-icon="mdi-lock-outline"
                  >{{ t("generic.file.cannotBeRemoved") }}
                </v-chip>

                <v-btn
                  v-else-if="!progressFor(file)"
                  icon="mdi-trash-can"
                  :size="isTouch ? 'default' : 'x-small'"
                  variant="text"
                  color="error"
                  @click.stop="onRemove(file)"
                />
              </div>
            </div>
          </template>

          <v-card
            min-width="300"
            max-width="500"
            rounded="lg"
            elevation="10"
          >
            <div
              v-if="getFileType(file) === EFileType.IMAGE"
              class="tw:flex tw:h-[300px] tw:w-full tw:items-center tw:justify-center tw:overflow-hidden tw:bg-surface-muted"
            >
              <app-img :src="file.file" />
            </div>

            <div
              v-else
              class="tw:flex tw:h-[140px] tw:w-full tw:flex-col tw:items-center tw:justify-center tw:gap-3 tw:bg-surface-muted tw:p-4"
            >
              <file-extension-icon
                :file="file"
                show-image
              />
              <div class="tw:text-xs tw:text-medium-emphasis">
                {{ t("generic.file.noInlinePreview") }}
              </div>
            </div>
          </v-card>
        </v-menu>
      </div>
    </div>
  </div>
</template>

<style src="@/assets/styles/components/uploadDropzone.scss" scoped lang="scss" />
